/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {PasswordInput} from '@mantine/core';
import React from 'react';
import {v4 as uuidv4} from 'uuid';

import BenchlingConfig from 'BenchlingConfig';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import Modal from 'ui-library/modals/Modal';
import ErrorSubclass from 'util/ErrorSubclass';

import createEsignature, {
  EsignatureConfig,
  SIGNATURE_TYPE_AZURE_AD,
  SIGNATURE_TYPE_OKTA,
  SIGNATURE_TYPE_SAML,
} from './createEsignature';
import {ESignatureInfo} from './ReviewConstants';

export class EsignFailureError extends ErrorSubclass {}

interface Props {
  esignatureConfig: EsignatureConfig;
  resolve: (info: ESignatureInfo) => void;
  reject: (...args: Array<any>) => any;
  onRequestClose: (...args: Array<any>) => any;
}

type State = any;

export class OktaEsignModal extends React.Component<Props, State> {
  static close(): void {
    LegacyModalContainer.close(OktaEsignModal);
  }

  static open(props: any): void {
    LegacyModalContainer.open(
      OktaEsignModal,
      <OktaEsignModal
        {...props}
        onRequestClose={() => {
          OktaEsignModal.close();
        }}
      />
    );
  }

  override state: State = {
    handle: EntityStore.currentUser().handle,
    password: '',
    submitting: false,
  };

  _handleHandleChange = (e: any): void => {
    this.setState({handle: e.target.value});
  };

  _handlePasswordChange = (e: any): void => {
    this.setState({password: e.target.value});
  };

  _handleReqestClose = (): void => {
    this.props.onRequestClose();
    this.props.reject();
  };

  _handleConfirmPassword = (): void => {
    this.setState({submitting: true});
    createEsignature(this.state.handle, this.state.password, this.props.esignatureConfig)
      .then((signature: ESignatureInfo) => {
        this.props.onRequestClose();
        this.props.resolve(signature);
      })
      .catch(() => {
        notifications.error('Could not verify your password. Please try again.');
      })
      .then(() => {
        this.setState({submitting: false});
      });
  };

  override render(): JSX.Element {
    return (
      <Modal
        buttons={[
          {
            text: this.state.submitting ? 'Confirming...' : 'Confirm password',
            style: ButtonStyle.SECONDARY,
            submit: true,
            disabled: !this.state.password || this.state.submitting,
          },
        ]}
        onRequestSubmit={this._handleConfirmPassword}
        onRequestClose={this._handleReqestClose}
        title="Confirm password to e-sign"
      >
        <FormGroup>
          <TextField
            label="Handle"
            labelStyle={LABEL_STYLES.LABEL}
            value={this.state.handle}
            onChange={this._handleHandleChange}
          />
        </FormGroup>
        <FormGroup>
          <PasswordInput
            autoComplete="off"
            autoFocus={true}
            label="Password"
            onChange={this._handlePasswordChange}
            value={this.state.password}
          />
        </FormGroup>
      </Modal>
    );
  }
}

function handleAzureADEsignature(esignatureConfig: any, resolve: any, reject: any): void {
  window.open('/ext/esig/azure-ad-oauth/start', '_blank', 'height=500,width=600');
  reject(); // TODO(BNCH-690): Handle microsoft oauth redirect back
}

const SAML_ESIGNATURE_CHANNEL = 'benchling.esignature.saml';

// Exported for testing
export const SAML_ESIGNATURE_TIMEOUT_MS = 300000; // 5 minutes

// Cancels any pending SAML esignature flow when a new one is started.
// This mirrors the accidental safety of the old window.samlEsignatureCallback singleton:
// assigning a new callback overwrote the old one. Without this, a BroadcastChannel orphaned
// from a dismissed modal could fire alongside the new modal's channel, triggering two API
// calls and creating duplicate reviewer rows.
let cancelPendingSamlEsignature: (() => void) | null = null;

// Exported for testing
export function handleSamlEsignature(esignatureConfig: any, resolve: any, reject: any): void {
  // Cancel any previous pending SAML flow before starting a new one
  cancelPendingSamlEsignature?.();

  // Use BroadcastChannel if both browser supports it AND config flag is enabled
  const useBroadcastChannel =
    typeof BroadcastChannel !== 'undefined' && BenchlingConfig.ENABLE_ELN_ESIGNATURE_BROADCAST_CHANNEL;

  // Generate a per-flow nonce to embed in the popup URL. The backend threads it through
  // SAML RelayState and echoes it in the BroadcastChannel payload, letting us ignore
  // completions intended for other tabs or stale flows.
  const state = useBroadcastChannel ? uuidv4() : null;
  const samlWindow = window.open(
    state != null
      ? `/ext/esignature/saml:begin?state=${encodeURIComponent(state)}`
      : '/ext/esignature/saml:begin',
    '_blank',
    'height=500,width=700'
  );

  if (samlWindow == null) {
    reject(
      new EsignFailureError(
        'Could not open authentication window. Please check that popups are not blocked and try again.'
      )
    );
    return;
  }

  if (useBroadcastChannel) {
    const channel = new BroadcastChannel(SAML_ESIGNATURE_CHANNEL);
    let timeoutTimer: ReturnType<typeof setTimeout>;
    let pollTimer: ReturnType<typeof setInterval>;
    let settled = false;

    const cleanup = (): void => {
      settled = true;
      cancelPendingSamlEsignature = null;
      channel.onmessage = null;
      channel.close();
      clearTimeout(timeoutTimer);
      clearInterval(pollTimer);
      try {
        samlWindow.close();
      } catch (e) {
        // Ignore errors from closing an already-closed window
        if (!(e instanceof DOMException)) {
          throw e;
        }
      }
    };

    cancelPendingSamlEsignature = (): void => {
      cleanup();
      reject(
        new EsignFailureError(
          'A new authentication attempt was started. Please complete sign-in in the new window.'
        )
      );
    };

    channel.onmessage = (event: MessageEvent) => {
      if (settled) {
        return;
      }
      if (event.data.type === 'saml_esignature_complete' && event.data.state === state) {
        cleanup();
        resolve({
          signature: event.data.signature,
          signatureType: SIGNATURE_TYPE_SAML,
        });
      }
    };

    // Poll for manual window closure to provide immediate feedback
    pollTimer = setInterval(() => {
      if (settled) {
        return;
      }
      if (samlWindow.closed) {
        cleanup();
        reject(
          new EsignFailureError(
            'Authentication window was closed before sign-in was complete. Please try again.'
          )
        );
      }
    }, 1000);

    timeoutTimer = setTimeout(() => {
      if (settled) {
        return;
      }
      cleanup();
      reject(new EsignFailureError('Authentication timed out. Please try again.'));
    }, SAML_ESIGNATURE_TIMEOUT_MS);
  } else {
    // Fallback for browsers that don't support BroadcastChannel
    const timer = setInterval(() => {
      if (samlWindow.closed) {
        clearInterval(timer);
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'samlEsignatureCallback' does not exist o... Remove this comment to see the full error message
        delete window.samlEsignatureCallback;
        reject(
          new EsignFailureError(
            'Authentication window was closed before sign-in was complete. Please try again.'
          )
        );
      }
    }, 1000);

    // @ts-expect-error ts-migrate(2339) FIXME: Property 'samlEsignatureCallback' does not exist o... Remove this comment to see the full error message
    window.samlEsignatureCallback = function (message: any) {
      clearInterval(timer);
      samlWindow.close();
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'samlEsignatureCallback' does not exist o... Remove this comment to see the full error message
      delete window.samlEsignatureCallback;
      resolve({
        signature: message,
        signatureType: SIGNATURE_TYPE_SAML,
      });
    };
  }
}

export default function requireEsignature(): Promise<ESignatureInfo | null> {
  if (!BenchlingConfig.ENABLE_ELN_ESIGNATURE) {
    return Promise.resolve(null);
  }
  const esignatureConfig = BenchlingConfig.ELN_ESIGNATURE_CONFIG;
  return new Promise((resolve, reject) => {
    switch (esignatureConfig!.type) {
      case SIGNATURE_TYPE_AZURE_AD:
        handleAzureADEsignature(esignatureConfig, resolve, reject);
        break;
      case SIGNATURE_TYPE_OKTA:
        OktaEsignModal.open({esignatureConfig, resolve, reject});
        break;
      case SIGNATURE_TYPE_SAML:
        handleSamlEsignature(esignatureConfig, resolve, reject);
        break;
      default:
        throw new Error(`Unexpected esignature type (${esignatureConfig!.type})`);
    }
  });
}
